import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useCompetencyStore = create((set) => ({
  competencyLoading: false,
  totalTests: 0,
  competencyTests: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get competency tests
  getCompetencyTest: async ({ query = null, successCallback }) => {
    set({
      competencyLoading: true,
    });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_TEST,
        null,
        query
      );

      if (response.success) {
        set({
          totalTests: response.data?.pagination?.totalTests || 0,
          competencyTests: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalpages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({
        competencyLoading: false,
      });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get competency tests by user
  getCompetencyTestByUserId: async ({ id, query = null, successCallback }) => {
    set({ competencyLoading: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_TEST_BY_USER,
        null,
        query,
        id
      );
      if (response.success) {
        set({
          competencyTests: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ competencyLoading: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create the competency test
  createCompetencyTest: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_TEST,
        data
      );

      if (response.success) {
        await useCompetencyStore.getState().getCompetencyTest({
          successCallback: () => {
            useCompetencyStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete competency test
  deleteCompetencyTest: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_TEST,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedTests = state.competencyTests.filter(
            (test) => test.id !== id
          );
          const updatedTotalItems = updatedTests.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            competencyTests: updatedTests,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete soft competency test
  deleteSoftCompetencyTest: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.SOFT_DELETE_TEST,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedTests = state.competencyTests.filter(
            (test) => test.id !== id
          );
          const updatedTotalItems = updatedTests.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            competencyTests: updatedTests,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update competency test
  updateCompetencyTest: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_TEST,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          // Find the updated competency test
          const updatedTest = state.competencyTests.find(
            (test) => test.id === id
          );

          // If the test is found, update it and move it to the first position
          if (updatedTest) {
            const updatedTests = [
              { ...updatedTest, ...response.data.data }, // Updated test
              ...state.competencyTests.filter((test) => test.id !== id), // Rest of the tests
            ];

            return {
              competencyTests: updatedTests,
              pagination: {
                ...state.pagination,
                currentPage: 1,
              },
            };
          }

          // If the test is not found, return the state as is
          return state;
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to add competency test question
  createCompetencyTestQuestion: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.ADD_TEST_QUESTION,
        data
      );

      if (response.success) {
        set((state) => {
          const updatedTests = state.competencyTests.map((test) => {
            if (test.id === data.testId) {
              return {
                ...test,
                questions: [
                  ...(test.questions || []),
                  ...(Array.isArray(response.data?.data)
                    ? response.data.data
                    : [response.data?.data]),
                ],
              };
            }
            return test;
          });

          return {
            competencyTests: updatedTests,
          };
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useCompetencyStore;
